import {Observable, catchError, forkJoin, map, of, take} from 'rxjs';

import {environment} from '../../../environments/environment';

export const APP_ROLES = {
  SUPER_ADMIN: 'SUPER_ADMIN',
  ADMIN: 'ADMIN',
  OFFICER: 'OFFICER',
  ACFS_OFFICER: 'ACFS_OFFICER',
  USER: 'USER',
} as const;

type AppRole = typeof APP_ROLES[keyof typeof APP_ROLES];

export const ADMIN_ROLES = [APP_ROLES.SUPER_ADMIN, APP_ROLES.ADMIN] as const;
export const STAFF_ROLES = [
  APP_ROLES.SUPER_ADMIN,
  APP_ROLES.ADMIN,
  APP_ROLES.OFFICER,
  APP_ROLES.ACFS_OFFICER,
] as const;

/**
 * Menu permissions from the CSV's four primary visibility columns:
 * SUPER_ADMIN, ADMIN, OFFICER and ACFS_OFFICER.
 *
 * The separate agency-level notes in the CSV are not application roles and
 * therefore are intentionally not used for e-Coding menu visibility.
 */
export const MENU_ROLES = {
  portal: STAFF_ROLES,

  certificateRequest: STAFF_ROLES,
  certificateWaitCheck: STAFF_ROLES,
  certificateAllRequests: STAFF_ROLES,
  certificateCodeSearch: STAFF_ROLES,
  thaiTracesSearch: STAFF_ROLES,

  importerCode: STAFF_ROLES,
  importDeclarations: STAFF_ROLES,
  importerCodeAssignment: STAFF_ROLES,
  importerSearch: STAFF_ROLES,
  tasLicenseFetch: STAFF_ROLES,
  tasLicenseSearch: STAFF_ROLES,

  masterData: ADMIN_ROLES,
  standardTypes: ADMIN_ROLES,
  standardCodes: ADMIN_ROLES,
  productGroupTypes: ADMIN_ROLES,
  productGroups: ADMIN_ROLES,
  productCodes: ADMIN_ROLES,
  productCodeSubs: ADMIN_ROLES,
  provinces: ADMIN_ROLES,
  districts: ADMIN_ROLES,
  subdistricts: ADMIN_ROLES,
  postcodes: ADMIN_ROLES,
  country: ADMIN_ROLES,
  businessTypes: ADMIN_ROLES,
  departments: ADMIN_ROLES,
  subCbs: ADMIN_ROLES,
  units: ADMIN_ROLES,
  certificateAuthority: ADMIN_ROLES,
  wsSettings: ADMIN_ROLES,
  wss: ADMIN_ROLES,
  settingBans: ADMIN_ROLES,

  settings: STAFF_ROLES,
  activityLogs: ADMIN_ROLES,
  lineSettings: ADMIN_ROLES,
  logRetention: ADMIN_ROLES,
  tasLicenseMokoso2: STAFF_ROLES,
  cabPermitApproved: STAFF_ROLES,
  tas2goStandard: STAFF_ROLES,
  gdxJuristic: STAFF_ROLES,
  gdxCooperative: STAFF_ROLES,
  gdxSmce: STAFF_ROLES,
  smceScrape: ADMIN_ROLES,
  userLk2Token: STAFF_ROLES,

  reports: STAFF_ROLES,
  tasLogs: ADMIN_ROLES,
  thaiTracesReqLog: ADMIN_ROLES,
  wsLogs: ADMIN_ROLES,
  thaiTracesDeliveryQueue: ADMIN_ROLES,
  signLog: ADMIN_ROLES,
  activityLogReport: ADMIN_ROLES,
  systemUsageSummary: STAFF_ROLES,

  campaigns: STAFF_ROLES,
  chatHistory: STAFF_ROLES,
  articles: STAFF_ROLES,
  survey: STAFF_ROLES,
} as const;

export type MenuKey = keyof typeof MENU_ROLES;

const ROLE_ALIASES: Record<string, AppRole> = {
  SUPER_USER: APP_ROLES.SUPER_ADMIN,
  ADMIN_SUPER: APP_ROLES.SUPER_ADMIN,
};

const KNOWN_ROLES = new Set<string>(Object.values(APP_ROLES));
const ROLE_PREFIXES: readonly AppRole[] = [
  APP_ROLES.SUPER_ADMIN,
  APP_ROLES.ACFS_OFFICER,
  APP_ROLES.OFFICER,
  APP_ROLES.ADMIN,
  APP_ROLES.USER,
];

type RoleRecord = Record<string, unknown>;

function isRecord(value: unknown): value is RoleRecord {
  return typeof value === 'object' && value !== null;
}

function normalizeRole(value: string): string {
  const normalized = value.trim().toUpperCase().replace(/^ROLE_/, '');
  return ROLE_ALIASES[normalized] ?? normalized;
}

function canonicalRole(value: string): AppRole | undefined {
  const normalized = normalizeRole(value);
  if (KNOWN_ROLES.has(normalized)) {
    return normalized as AppRole;
  }

  return ROLE_PREFIXES.find(role => normalized.startsWith(`${role}_`));
}

function addRoleString(value: string, roles: Set<string>): void {
  const normalized = normalizeRole(value);
  const candidates = normalized.split(/[^A-Z0-9_]+/);

  for (const candidate of candidates) {
    const role = canonicalRole(candidate);
    if (role) {
      roles.add(role);
    }
  }
}

function collectRoleValues(value: unknown, roles: Set<string>): void {
  if (typeof value === 'string') {
    addRoleString(value, roles);
    return;
  }

  if (Array.isArray(value)) {
    value.forEach(item => collectRoleValues(item, roles));
    return;
  }

  if (isRecord(value)) {
    collectRoleValues(value['authority'], roles);
    collectRoleValues(value['role'], roles);
  }
}

export function extractRoles(userData: unknown): ReadonlySet<string> {
  const roles = new Set<string>();
  if (!isRecord(userData)) {
    return roles;
  }

  collectRoleValues(userData['roles'], roles);
  collectRoleValues(userData['role'], roles);
  collectRoleValues(userData['authorities'], roles);

  const realmAccess = userData['realm_access'];
  if (isRecord(realmAccess)) {
    collectRoleValues(realmAccess['roles'], roles);
  }

  const resourceAccess = userData['resource_access'];
  if (isRecord(resourceAccess)) {
    const tokenClientId = typeof userData['azp'] === 'string' ? userData['azp'] : undefined;
    const clientAccess = resourceAccess[environment.clientId] ??
      (tokenClientId ? resourceAccess[tokenClientId] : undefined);
    if (isRecord(clientAccess)) {
      collectRoleValues(clientAccess['roles'], roles);
    }
  }

  return roles;
}

export function extractRolesFromAccessToken(accessToken: string | null | undefined): ReadonlySet<string> {
  if (!accessToken) {
    return new Set<string>();
  }

  try {
    const encodedPayload = accessToken.split('.')[1];
    if (!encodedPayload || typeof atob !== 'function') {
      return new Set<string>();
    }

    const base64Payload = encodedPayload.replace(/-/g, '+').replace(/_/g, '/');
    const paddedPayload = base64Payload.padEnd(Math.ceil(base64Payload.length / 4) * 4, '=');
    return extractRoles(JSON.parse(atob(paddedPayload)));
  } catch {
    return new Set<string>();
  }
}

export function mergeRoles(...roleSets: ReadonlySet<string>[]): ReadonlySet<string> {
  const roles = new Set<string>();
  roleSets.forEach(roleSet => roleSet.forEach(role => roles.add(role)));
  return roles;
}

export function hasAnyRoleInSet(roles: ReadonlySet<string>, allowedRoles: readonly string[]): boolean {
  return allowedRoles.some(allowedRole => {
    const expectedRole = canonicalRole(allowedRole);
    return expectedRole !== undefined && [...roles].some(role => canonicalRole(role) === expectedRole);
  });
}

export function hasAnyRole(userData: unknown, allowedRoles: readonly string[]): boolean {
  return hasAnyRoleInSet(extractRoles(userData), allowedRoles);
}

export interface RoleSource {
  getUserData(): Observable<unknown>;
  getAccessToken?: () => Observable<string>;
}

/** Load roles from both user-info claims and the access-token claims. */
export function loadUserRoles(source: RoleSource): Observable<ReadonlySet<string>> {
  const userData$ = source.getUserData().pipe(
    take(1),
    catchError(() => of(null)),
  );
  const accessToken$ = typeof source.getAccessToken === 'function'
    ? source.getAccessToken().pipe(
      take(1),
      catchError(() => of('')),
    )
    : of('');

  return forkJoin({userData: userData$, accessToken: accessToken$}).pipe(
    map(({userData, accessToken}) => mergeRoles(
      extractRoles(userData),
      extractRolesFromAccessToken(accessToken),
    )),
  );
}
